import * as React from 'react';

import { ThemeConfigurationContext } from './configuration';
import type {
    ResolvedThemedStyleSheet,
    ThemedNamedStyles,
    ThemedStyleSheet,
} from './ThemedStyleSheet';
import { getResolvedTheme, type Theme } from './theme';
import {
    DynamicTokenOverridesContext,
    type DynamicTokens,
    type DynamicTokensOverrides,
    getDynamicTokens,
} from './tokens';
import {
    deKeyVariantsValue,
    forEachVariants,
    type KeyedVariantsValue,
    keyVariantsValue,
    type VariantsValue,
} from './variants';

const LayoutThemeContext = React.createContext<undefined | Partial<VariantsValue>>(undefined);

/**
 * Resolve a themed style sheet with a theme and dynamic tokens
 */
function resolveThemedStyleSheet<T extends ThemedNamedStyles>(
    themedStyleSheet: ThemedStyleSheet<T>,
    theme: KeyedVariantsValue,
    dynamicTokens: DynamicTokens
): ResolvedThemedStyleSheet<T> {
    const styleSheet = themedStyleSheet[theme];

    return Object.keys(styleSheet).reduce(
        (prev, k) => {
            const style = styleSheet[k];
            if (Array.isArray(style)) {
                // resolve the dynamic part of the style
                // @ts-ignore
                prev[k] = [style[0], style[1](dynamicTokens)];
            } else {
                // @ts-ignore
                prev[k] = style;
            }
            return prev;
        },
        {} as ResolvedThemedStyleSheet<T>
    );
}

const themedFontsCache = new WeakMap<
    ThemedStyleSheet<any>,
    WeakMap<DynamicTokensOverrides, Map<KeyedVariantsValue, ResolvedThemedStyleSheet<any>>>
>();

/**
 * Resolve a themed style sheet with a theme and dynamic tokens.
 * If we haven't computed it yet, we compute the stylesheet for all theme variants and we cache it.
 */
function resolveThemedStyleSheetWithCache<T extends ThemedNamedStyles>(
    themedStyleSheet: ThemedStyleSheet<T>,
    theme: KeyedVariantsValue,
    dynamicTokensOverrides: DynamicTokensOverrides
): ResolvedThemedStyleSheet<T> {
    let cache = themedFontsCache.get(themedStyleSheet);

    if (cache) {
        const existing = cache.get(dynamicTokensOverrides);
        if (existing) {
            return existing.get(theme)!;
        }
    } else {
        cache = new WeakMap<
            DynamicTokensOverrides,
            Map<KeyedVariantsValue, ResolvedThemedStyleSheet<any>>
        >();
        themedFontsCache.set(themedStyleSheet, cache);
    }

    const resolvedThemeSheet = new Map<KeyedVariantsValue, ResolvedThemedStyleSheet<T>>();

    const dynamicTokens = getDynamicTokens(dynamicTokensOverrides);

    forEachVariants((_variant, keyedVariant) => {
        resolvedThemeSheet.set(
            keyedVariant,
            // @ts-ignore
            resolveThemedStyleSheet(themedStyleSheet, keyedVariant, dynamicTokens)
        );
    });

    cache.set(dynamicTokensOverrides, resolvedThemeSheet);

    return resolvedThemeSheet.get(theme)!;
}

/**
 * Return the theme being configured (in the app or system).
 */
export function useConfigurationTheme(): VariantsValue {
    const configState = React.useContext(ThemeConfigurationContext);
    const theme = configState.app || configState.system;
    return React.useMemo(() => deKeyVariantsValue(theme), [theme]);
}

/**
 * Return the current variants
 */
export function useThemeVariants(): VariantsValue {
    const configTheme = useConfigurationTheme();
    const layoutTheme = React.useContext(LayoutThemeContext);

    return React.useMemo(
        () => ({
            ...configTheme,
            ...layoutTheme,
        }),
        [configTheme, layoutTheme]
    );
}
/**
 * Return the current theme to use in the UI.
 */
export function useCurrentTheme(): [Theme, undefined];
export function useCurrentTheme<T extends ThemedNamedStyles>(
    themedStyleSheet: ThemedStyleSheet<T>,
    options?: { theme?: Partial<VariantsValue> }
): [Theme, ResolvedThemedStyleSheet<T>];
export function useCurrentTheme(
    themedStyleSheet: undefined,
    options?: { theme?: Partial<VariantsValue> }
): [Theme, undefined];
export function useCurrentTheme<T extends ThemedNamedStyles>(
    themedStyleSheet?: ThemedStyleSheet<T>,
    options: {
        /** Overwrite theme to select */
        theme?: VariantsValue;
    } = {}
) {
    const variants = useThemeVariants();
    const dynamicTokensOverrides = React.useContext(DynamicTokenOverridesContext);

    const theme = React.useMemo(() => {
        return keyVariantsValue({
            ...variants,
            ...options.theme,
        });
    }, [variants, options.theme?.color, options.theme?.contrast]);

    const resolvedTheme = getResolvedTheme(theme, dynamicTokensOverrides);

    const resolvedStyleSheet: ResolvedThemedStyleSheet<T> | undefined = React.useMemo(() => {
        if (!themedStyleSheet) {
            return undefined;
        }

        return resolveThemedStyleSheetWithCache(themedStyleSheet, theme, dynamicTokensOverrides);
    }, [theme, themedStyleSheet, dynamicTokensOverrides]);

    return [resolvedTheme, resolvedStyleSheet];
}

/**
 * Hook to check if an the in-app theme, so not the system theme, is configured.
 */
export function useInAppThemeConfigured() {
    const configState = React.useContext(ThemeConfigurationContext);

    return configState.app !== undefined && configState.app !== null;
}

/**
 * Reset the layout theme to use the configuration.
 */
export function ResetLayoutTheme(props: { children: React.ReactNode }): React.ReactElement {
    const { children } = props;

    return <LayoutThemeContext.Provider value={undefined}>{children}</LayoutThemeContext.Provider>;
}

/**
 * Define the palette/theme for the UI
 */
export function LayoutTheme(props: {
    theme: Partial<VariantsValue>;
    children: React.ReactNode;
}): React.ReactElement {
    const { theme, children } = props;

    const parentTheme = React.useContext(LayoutThemeContext);
    const value = React.useMemo(() => {
        return {
            ...parentTheme,
            ...theme,
        };
    }, [parentTheme, theme.color, theme.contrast, theme.radius]);

    return <LayoutThemeContext.Provider value={value}>{children}</LayoutThemeContext.Provider>;
}
